import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { demoSession, demoUser, isDemoMode } from '../lib/demo/harness';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export type DemoShellAuthState =
  | 'ready'
  | 'unauthorized'
  | 'sign-out-failed-once';

const DEMO_SHELL_AUTH_STATES = new Set<DemoShellAuthState>([
  'ready',
  'unauthorized',
  'sign-out-failed-once',
]);

/**
 * A narrowly gated route fixture for shell/auth recovery evidence.
 * Passing the booleans explicitly keeps the production boundary unit-testable.
 */
export function resolveDemoShellAuthState(
  raw: string | null,
  development = import.meta.env.DEV,
  demoMode = isDemoMode(),
): DemoShellAuthState | null {
  if (!development || !demoMode || !raw) return null;
  return DEMO_SHELL_AUTH_STATES.has(raw as DemoShellAuthState)
    ? (raw as DemoShellAuthState)
    : null;
}

function currentDemoShellAuthState(): DemoShellAuthState {
  if (typeof window === 'undefined') return 'ready';
  return (
    resolveDemoShellAuthState(
      new URLSearchParams(window.location.search).get('shellAuthState'),
    ) ?? 'ready'
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isDemoMode()) {
    return <DemoAuthProvider>{children}</DemoAuthProvider>;
  }
  return <LiveAuthProvider>{children}</LiveAuthProvider>;
}

function DemoAuthProvider({ children }: { children: ReactNode }) {
  const fixture = currentDemoShellAuthState();
  const initiallyAuthorized = fixture !== 'unauthorized';
  const [user, setUser] = useState<User | null>(
    initiallyAuthorized ? demoUser : null,
  );
  const [session, setSession] = useState<Session | null>(
    initiallyAuthorized ? demoSession : null,
  );
  const signOutAttemptRef = useRef(0);

  const signOut = useCallback(async () => {
    const attempt = signOutAttemptRef.current;
    signOutAttemptRef.current += 1;
    // Keep the pending state visible long enough for deterministic semantic
    // and visual evidence while still feeling immediate in the dev harness.
    await new Promise((resolve) => setTimeout(resolve, 500));
    if (fixture === 'sign-out-failed-once' && attempt === 0) {
      throw new Error(
        'Development-only provider detail: tenant=private auth_adapter=demo',
      );
    }
    setUser(null);
    setSession(null);
  }, [fixture]);

  const value = useMemo<AuthState>(
    () => ({ user, session, loading: false, signOut }),
    [session, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function LiveAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // `getSession()` and the INITIAL_SESSION event can resolve in either
    // order. Once an auth event has supplied the current session, a slower
    // initial read must not overwrite it with a stale value.
    let authEventSeen = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      if (cancelled) return;
      authEventSeen = true;
      // Supabase fires INITIAL_SESSION on every page load and TOKEN_REFRESHED
      // periodically with the same underlying user. Skip the setState calls
      // when nothing meaningful changed — every churn here is a fresh object
      // identity propagated through the provider value, which re-renders
      // every consumer of useAuth() (ProtectedRoute, ChatLayout, …) and
      // re-fires their effects. The chat surface is sensitive to that
      // because some effects load conversation state from the DB.
      //
      // Compare (id, updated_at) so USER_UPDATED events (email/profile/
      // metadata changes that keep the same id but bump updated_at) still
      // propagate. An id-only guard would silently drop those and leave
      // useAuth().user stale until a full reload.
      setUser((prev) => {
        const next = s?.user ?? null;
        if (!prev && !next) return prev;
        if (!prev || !next) return next;
        if (prev.id === next.id && prev.updated_at === next.updated_at) return prev;
        return next;
      });
      setSession((prev) => {
        if (!prev && !s) return prev;
        if (!prev || !s) return s;
        // access_token changes on every TOKEN_REFRESHED with new credentials;
        // user.updated_at covers USER_UPDATED that arrives without a token
        // rotation. Either differing → propagate.
        if (
          prev.access_token === s.access_token
          && prev.user?.id === s.user?.id
          && prev.user?.updated_at === s.user?.updated_at
        ) {
          return prev;
        }
        return s;
      });
      setLoading(false);
    });

    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        if (cancelled || authEventSeen) return;
        setUser(s?.user ?? null);
        setSession(s);
      })
      .catch((error: unknown) => {
        // A storage/provider failure should degrade to signed-out rather than
        // leave every protected route on an infinite loading spinner.
        if (!cancelled && !authEventSeen) {
          // Do not print provider/storage diagnostics; those can contain
          // tenant configuration or token-adapter detail in the browser.
          void error;
          console.error('Could not restore authentication session.');
          setUser(null);
          setSession(null);
        }
      })
      .finally(() => {
        if (!cancelled && !authEventSeen) setLoading(false);
      });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  // Stable provider value: without `useMemo`, the value object is rebuilt
  // every render and every consumer of useAuth re-renders downstream
  // — even when user / session / loading are referentially unchanged.
  const value = useMemo<AuthState>(
    () => ({ user, session, loading, signOut }),
    [user, session, loading, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
